import { useCallback, useState } from "react";
import { compareDashboardValues, toggleTableSort, type SortDirection } from "./tableSortUtils";

export type DashboardTableColumnSort<K extends string = string> = {
  key: K;
  direction: SortDirection;
};

/**
 * Client-side column sort state for dashboard tables (pairs with {@link DashboardDataTableSortButton}).
 */
export function useDashboardTableSort<K extends string>(initial: DashboardTableColumnSort<K>) {
  const [sort, setSort] = useState<DashboardTableColumnSort<K>>(initial);

  const toggleColumn = useCallback((key: K) => {
    setSort(cur => toggleTableSort(cur, key));
  }, []);

  return { sort, setSort, toggleColumn };
}

/**
 * Returns a sorted copy of `rows` by the active column in `sort`, using per-key getters.
 */
export function sortRowsByKeyAccessor<T, K extends string>(
  rows: readonly T[],
  sort: DashboardTableColumnSort<K>,
  getters: Record<K, (row: T) => string | number | null | undefined>,
): T[] {
  const get = getters[sort.key];
  if (!get) {
    return [...rows];
  }
  return [...rows].sort((a, b) => compareDashboardValues(get(a), get(b), sort.direction));
}
